export const DEFAULT_PROPOSAL_PARAMETERS = Object.freeze({
  cbs: 0.113, ibs: 0.001, equipmentIcms: 0.18, serviceIss: 0.05,
  administrativeExpense: 0.15, equipmentIrpj: 0.012,
  equipmentCsll: 0.0108, serviceIrpj: 0.048, serviceCsll: 0.0288,
});

export const proposalParameterKeys = Object.keys(DEFAULT_PROPOSAL_PARAMETERS);

export function normalizeProposalParameters(input = {}) {
  const result = {};
  for (const key of proposalParameterKeys) {
    const value = Number(input[key] ?? DEFAULT_PROPOSAL_PARAMETERS[key]);
    if (!Number.isFinite(value) || value < 0 || value >= 1) throw new Error(`invalid_parameter_${key}`);
    result[key] = value;
  }
  return result;
}

export function calculateProposalItem(item, parameters = DEFAULT_PROPOSAL_PARAMETERS) {
  const p = normalizeProposalParameters(parameters);
  const quantity = Number(item.quantity);
  const costUnit = Number(item.costUnit);
  const inputValue = Number(item.inputValue);
  const creditPurchase = Number(item.creditPurchase || 0);
  if (![quantity, costUnit, inputValue, creditPurchase].every(Number.isFinite) || quantity <= 0 || costUnit < 0 || creditPurchase < 0)
    throw new Error('invalid_pricing_input');
  const service = item.classification === 'service';
  if (!service && item.classification !== 'equipment') throw new Error('invalid_classification');
  const localRate = service ? p.serviceIss : p.equipmentIcms;
  const incomeRate = service ? p.serviceIrpj + p.serviceCsll : p.equipmentIrpj + p.equipmentCsll;
  const salesRate = (p.cbs + p.ibs) * (1 - localRate) / (1 + p.cbs + p.ibs);
  const burden = p.administrativeExpense + localRate + incomeRate + salesRate;
  let markup;
  if (item.pricingMode === 'unit_price') {
    if (inputValue < 0) throw new Error('invalid_unit_price');
    markup = costUnit > 0 ? inputValue / costUnit : 0;
  } else if (item.pricingMode === 'margin') {
    if (costUnit <= 0) throw new Error('invalid_pricing_input');
    if (inputValue < 0 || inputValue >= 1 || 1 - burden - inputValue <= 0) throw new Error('unfeasible_margin');
    markup = (1 - creditPurchase / (quantity * costUnit)) / (1 - burden - inputValue);
  } else if (item.pricingMode === 'markup') {
    if (costUnit <= 0) throw new Error('invalid_pricing_input');
    if (inputValue <= 1) throw new Error('invalid_markup');
    markup = inputValue;
  } else throw new Error('invalid_pricing_mode');
  if (!Number.isFinite(markup) || (item.pricingMode !== 'unit_price' && markup <= 0)) throw new Error('unfeasible_margin');
  const unitPrice = item.pricingMode === 'unit_price' ? inputValue : costUnit * markup;
  const totalPrice = quantity * unitPrice;
  const localTax = totalPrice * localRate;
  const base = (totalPrice - localTax) / (1 + p.cbs + p.ibs);
  const cbs = base * p.cbs;
  const ibs = base * p.ibs;
  const incomeTax = totalPrice * incomeRate;
  const administrativeExpense = totalPrice * p.administrativeExpense;
  const salesTaxes = cbs + ibs + localTax + incomeTax;
  const profit = totalPrice - quantity * costUnit - salesTaxes - administrativeExpense + creditPurchase;
  return { markup, margin: totalPrice ? profit / totalPrice : 0, unitPrice, totalPrice,
    creditPurchase, cbs, ibs, localTax, incomeTax, administrativeExpense, salesTaxes, profit };
}

export function calculateProposalTotals(items, parameters = DEFAULT_PROPOSAL_PARAMETERS) {
  const rows = items.map(item => calculateProposalItem(item, parameters));
  const totals = Object.fromEntries(['totalPrice','creditPurchase','cbs','ibs','localTax','incomeTax','administrativeExpense','salesTaxes','profit'].map(key => [key, rows.reduce((sum,row) => sum + row[key], 0)]));
  return { rows, ...totals, margin: totals.totalPrice ? totals.profit / totals.totalPrice : 0 };
}
